/** ARV Engine (v1 "AI Estimator" tab — detail-arv.js aiPanel/arvRun/arvCompute,
 * verbatim semantics):
 *
 *   - GET /api/arv/estimate?address&sqft&beds&zip_code — the SAME backend
 *     endpoint v1 calls. The server returns sold comps (Zillow + Realtor.com)
 *     with the "renovated set" (top-half $/sqft) pre-included.
 *   - ARV = median $/sqft of the INCLUDED comps × subject sqft; low/high =
 *     quartile ppsf × sqft. Kick out a comp → the number recomputes.
 *   - Max offer here is v1's 70% rule (0.70 × ARV − rehab) — deliberately
 *     different from the Deal Engine's 0.72 MAO; both exist in v1.
 *   - Shared state contract: edits.arvResult (the whole response, with
 *     per-comp `included` flags) + edits.arvRehab (raw string) + edits.arv
 *     ("$X,XXX" on apply). v1 reads/writes the SAME keys, so an analysis run
 *     in v2 shows up in v1's AI tab and vice versa. arvResult persists via a
 *     Phase 12 rep patch (whole-object replace — no merge surprises).
 */
import { backendFetch, type BackendOptions } from "../../lib/backend";
import { natKey } from "../../lib/natkey";
import type { RecOp } from "../../lib/outbox";

export interface ArvComp {
  address: string;
  price: number;
  ppsf: number;
  sqft: number;
  beds: number | null;
  baths: number | null;
  sold_date: string | null;
  source: string;
  photo: string | null;
  link: string | null;
  included: boolean;
}

export interface ArvResult {
  comps: ArvComp[];
  subject: { sqft: number };
  sources: string[];
  confidence: string | null;
}

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

export function narrowArvComp(raw: unknown): ArvComp | null {
  if (!isObj(raw)) return null;
  const addr = typeof raw["address"] === "string" ? raw["address"] : "";
  if (!addr) return null;
  const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
  const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);
  return {
    address: addr,
    price: num(raw["price"]) ?? 0,
    ppsf: num(raw["ppsf"]) ?? 0,
    sqft: num(raw["sqft"]) ?? 0,
    beds: num(raw["beds"]),
    baths: num(raw["baths"]),
    sold_date: str(raw["sold_date"]),
    source: str(raw["source"]) ?? "zillow",
    photo: str(raw["photo"]),
    link: str(raw["link"]),
    included: raw["included"] === true,
  };
}

/** Tolerant read of edits.arvResult — written by v1 or v2. Junk → null. */
export function narrowArvResult(raw: unknown): ArvResult | null {
  if (!isObj(raw)) return null;
  const comps = Array.isArray(raw["comps"])
    ? (raw["comps"] as unknown[]).map(narrowArvComp).filter((c): c is ArvComp => c !== null)
    : [];
  const subjSqft = isObj(raw["subject"]) && typeof (raw["subject"] as Record<string, unknown>)["sqft"] === "number"
    ? ((raw["subject"] as Record<string, unknown>)["sqft"] as number)
    : 0;
  if (!comps.length && !subjSqft) return null;
  const sources = Array.isArray(raw["sources"]) ? (raw["sources"] as unknown[]).filter((s): s is string => typeof s === "string") : [];
  const conf = raw["confidence"];
  return {
    comps,
    subject: { sqft: subjSqft },
    sources,
    confidence: typeof conf === "string" ? conf : typeof conf === "number" ? String(conf) : null,
  };
}

/** v1 arvRun's zip extraction: the trailing 5 digits of the address. */
export const zipOfAddress = (address: string): string =>
  (String(address || "").match(/(\d{5})\s*$/) || [])[1] ?? "";

/** v1 arvRun's exact query string for GET /api/arv/estimate. */
export function arvQuery(address: string, sqft: number, beds: number | null): string {
  return (
    "address=" + encodeURIComponent(address || "") +
    "&sqft=" + sqft +
    "&beds=" + (beds || 0) +
    "&zip_code=" + zipOfAddress(address)
  );
}

export async function runArvEstimate(
  token: string | null,
  address: string,
  sqft: number,
  beds: number | null,
  opts?: Pick<BackendOptions, "signal" | "onWake">,
): Promise<ArvResult> {
  const j = await backendFetch<Record<string, unknown>>(token, "/api/arv/estimate?" + arvQuery(address, sqft, beds), { ...opts });
  const r = narrowArvResult(j);
  if (!r) throw new Error("No solds found nearby — try re-running, or the area may have thin sale activity.");
  return r;
}

// ---- the math (v1 arvCompute, verbatim) --------------------------------------

export interface ArvComputed {
  arv: number | null;
  low: number | null;
  high: number | null;
  ppsf: number | null;
  sel: number;
}

export function arvCompute(j: Pick<ArvResult, "comps" | "subject">): ArvComputed {
  const sel = (j.comps ?? []).filter((c) => c.included);
  if (!sel.length) return { arv: null, low: null, high: null, ppsf: null, sel: 0 };
  const pp = sel.map((c) => c.ppsf).sort((a, b) => a - b);
  const n = pp.length;
  const med = n % 2 ? (pp[(n - 1) / 2] as number) : ((pp[n / 2 - 1] as number) + (pp[n / 2] as number)) / 2;
  const sqft = j.subject?.sqft || 0;
  return {
    arv: Math.round(med * sqft),
    low: Math.round((pp[Math.max(0, Math.floor(n / 4))] as number) * sqft),
    high: Math.round((pp[Math.min(n - 1, Math.floor((3 * n) / 4))] as number) * sqft),
    ppsf: med,
    sel: sel.length,
  };
}

/** v1 arvRender's Max offer: 70% × ARV − rehab (display floors at 0). */
export function arvMao(arv: number | null, rehabRaw: unknown): number | null {
  if (!arv) return null;
  const rehab = parseFloat(String(rehabRaw ?? "").replace(/[^0-9.]/g, "")) || 0;
  return Math.round(arv * 0.7 - rehab);
}

// ---- persistence -------------------------------------------------------------

/** Whole-response persist (v1: p.edits.arvResult = j). rep = replace, so a
 * comp toggle can never half-merge with an older comps array. */
export function opSaveArvResult(address: string, result: ArvResult): RecOp {
  return { s: "lead_fields", op: "patch", k: natKey(address), d: { arvResult: result as unknown as Record<string, unknown> }, rep: ["arvResult"] };
}

export const ARV_MSG = {
  needSqft: "This lead has no sqft yet — hit Enrich first",
  unreachable: "Could not reach the backend.",
  saved: "ARV saved to the lead ✓",
  empty: "No solds found nearby — try re-running, or the area may have thin sale activity.",
};
